import { Box, Flex, Heading, Link, SkeletonText, Spacer, Text, VStack } from '@chakra-ui/react'
import { useAtom, useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'

import useColorModeValue from '@/hooks/useColorModeValue'
import { loadingAtom, newsAtom, showAllAtom } from '@/store/news'
import { INewsItem } from '@/types/news'

type TRightMoreNewsProps = {
    moreNews: INewsItem[]
    onCardClick: (id: string) => void
}

export default function RightMoreNewsSection({ moreNews, onCardClick }: TRightMoreNewsProps) {
    const newsData: INewsItem[] = useAtomValue(newsAtom)
    const [showAll, setShowAll] = useAtom(showAllAtom)
    const loading = useAtomValue(loadingAtom)
    const INITIAL_MORE_NEWS_COUNT = 5
    const displayedNews = showAll ? newsData : newsData.slice(0, INITIAL_MORE_NEWS_COUNT)
    const { t } = useTranslation()
    const color = useColorModeValue('var(--chakra-colors-blue-900)', 'var(--chakra-colors-white)')
    const moreNewsSubtitleColor = useColorModeValue('var(--chakra-colors-gray-500)', 'var(--chakra-colors-white)')
    return (
        <Box flex="0.5" position={{ md: 'sticky' }} maxH={{ md: '90vh' }} overflowY={{ md: 'auto' }} overflowX="hidden" w="100%">
            {!loading && moreNews && moreNews?.length > 0 && (
                <Flex justifyContent="space-between" alignItems="center" mb={4}>
                    <Heading fontSize="md" fontWeight="700" color={color}>
                        {t('news.moreNews.title')}
                    </Heading>
                    {!loading && displayedNews.length >= INITIAL_MORE_NEWS_COUNT && (
                        <Link
                            color="orange.500"
                            fontSize="sm"
                            fontWeight="700"
                            onClick={() => setShowAll((prev) => !prev)}
                            cursor="pointer"
                        >
                            {showAll ? t('news.moreNews.showLess') : t('news.moreNews.showAll')}
                        </Link>
                    )}
                </Flex>
            )}

            <VStack gap={4}>
                {loading && <SkeletonText noOfLines={4} />}
                {!loading &&
                    displayedNews?.map((news) => (
                        <Box
                            key={news.id}
                            h="120px"
                            w="100%"
                            px="8px"
                            py="16px"
                            borderWidth="1px"
                            borderRadius="12px"
                            overflow="hidden"
                            borderColor="gray.200"
                            transition="all 0.3s"
                            alignItems="center"
                            alignSelf="stretch"
                            _hover={{
                                shadow: 'lg',
                                cursor: 'pointer',
                            }}
                            onClick={() => onCardClick(news.id)}
                        >
                            <Flex align="center">
                                {/* Left content */}
                                <Box>
                                    <Text fontWeight="700" lineHeight="22px" letterSpacing="0.25px" fontSize="16px" mb={1}>
                                        {news.title}
                                    </Text>
                                    <Text
                                        fontSize="14px"
                                        fontWeight="400"
                                        lineHeight="20px"
                                        letterSpacing="0.247px"
                                        color={moreNewsSubtitleColor}
                                    >
                                        {news.subtitle}
                                    </Text>
                                </Box>
                                <Spacer />
                            </Flex>
                        </Box>
                    ))}
            </VStack>
        </Box>
    )
}
